import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ══════════════════════════════════════════════════════════════════
   CYWERST APPAREL — DROP 001
   Products are imported LIVE from Printful (GET /api/public/apparel/products).
   Checkout goes through Stripe; on payment the backend creates a Printful
   order automatically (print + ship). Nothing hardcoded here.
   ══════════════════════════════════════════════════════════════════ */

const AP = {
  BG:'#08080D', S:'#101017', S2:'#15151d', LINE:'#22222c',
  INK:'#F4F3F7', SUB:'#8C8C99', ACC:'#A366FF', ACC2:'#F20CC9',
  GRAD:'linear-gradient(120deg,#F20CC9,#A366FF)',
  DISP:{ fontFamily:"'Manrope',sans-serif", fontWeight:800, letterSpacing:'-.02em', textTransform:'uppercase' },
};
const money = (n, c = 'EUR') => {
  try { return new Intl.NumberFormat('en-IE', { style:'currency', currency:(c || 'EUR') }).format(Number(n)); }
  catch { return `€${Number(n).toFixed(2)}`; }
};

/* ── Live products (Printful, via backend) ────────────────────────── */
let _pcache = null, _pprom = null;
function loadProducts() {
  if (_pcache) return Promise.resolve(_pcache);
  if (!_pprom) {
    _pprom = fetch('/api/public/apparel/products')
      .then(r => r.json())
      .then(d => { _pcache = d; return d; })
      .catch(() => ({ configured:false, products:[], error:'network' }));
  }
  return _pprom;
}
function useProducts() {
  const [s, setS] = useState(_pcache ? { loading:false, ..._pcache } : { loading:true, products:[] });
  useEffect(() => { let m = true; loadProducts().then(d => m && setS({ loading:false, ...d })); return () => { m = false; }; }, []);
  return s;
}
const findBySlug = (products, slug) => (products || []).find(p => p.slug === slug);

/* ── Cart (client-side, persisted) ────────────────────────────────── */
const CartCtx = createContext(null);
const KEY = 'cywerst_bag_v2';
const loadBag = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
const saveBag = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch {} };

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadBag);
  const [open, setOpen] = useState(false);
  useEffect(() => { saveBag(items); }, [items]);
  const add = (item) => {
    const k = String(item.variant_id);
    setItems(cur => {
      const ex = cur.find(i => i.key === k);
      if (ex) return cur.map(i => i.key === k ? { ...i, qty: i.qty + 1 } : i);
      return [...cur, { key:k, ...item, qty:1 }];
    });
    setOpen(true);
  };
  const setQty = (k, q) => setItems(cur => q <= 0 ? cur.filter(i => i.key !== k) : cur.map(i => i.key === k ? { ...i, qty:q } : i));
  const remove = (k) => setItems(cur => cur.filter(i => i.key !== k));
  const clear = () => setItems([]);
  const count = items.reduce((a, i) => a + i.qty, 0);
  const subtotal = items.reduce((a, i) => a + i.price * i.qty, 0);
  const currency = items[0]?.currency || 'EUR';
  return <CartCtx.Provider value={{ items, open, setOpen, add, setQty, remove, clear, count, subtotal, currency }}>{children}</CartCtx.Provider>;
}
const useCart = () => useContext(CartCtx);

/* ── Garment glyphs (backgrounds / fallbacks) ─────────────────────── */
const Garment = ({ kind = 'tee', style }) => kind === 'hoodie'
  ? <svg viewBox="0 0 100 100" style={style} fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M35 22c0 6 30 6 30 0M28 26 18 34l6 10 6-4v34h40V40l6 4 6-10-10-8-8-4c0 8-24 8-24 0z"/><path d="M50 26v14"/></svg>
  : <svg viewBox="0 0 100 100" style={style} fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M38 22 20 32l6 12 8-4v40h32V40l8 4 6-12-18-10-6 6c-3 3-9 3-12 0z"/></svg>;
const Frame = ({ src, alt, style }) => (
  <div style={{ position:'absolute',inset:0,background:AP.S,display:'flex',alignItems:'center',justifyContent:'center',...style }}>
    {src ? <img src={src} alt={alt||''} loading="lazy" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
         : <Garment style={{ width:'55%',color:'rgba(244,243,247,.10)' }} />}
  </div>
);

/* ══════════════════════ SHELL (header + bag drawer) ══════════════ */
const AP_NAV = [
  ['produtos','Products','/produtos'],
  ['servicos','Services','/servicos'],
  ['estudio','Studio','/estudio'],
  ['apparel','Apparel','/apparel'],
  ['contacto','Contact','/contacto'],
];
const AP_CSS = `
  .apx a{text-decoration:none}
  .apx .nl{font-size:13px;letter-spacing:.06em;text-transform:uppercase;color:${AP.SUB};font-weight:600;transition:color .15s}
  .apx .nl:hover{color:${AP.INK}}
  .apx .nl.on{color:${AP.INK}}
  .apx .bag{display:inline-flex;align-items:center;gap:8px;height:40px;padding:0 16px;border:1px solid ${AP.LINE};border-radius:999px;background:transparent;color:${AP.INK};font-size:13px;font-weight:700;letter-spacing:.04em;cursor:pointer;transition:border-color .18s}
  .apx .bag:hover{border-color:${AP.ACC}}
  .apx .acct{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border:1px solid ${AP.LINE};border-radius:999px;color:${AP.INK};transition:border-color .18s,color .18s}
  .apx .acct:hover{border-color:${AP.ACC};color:${AP.ACC}}
  .apx .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;height:52px;padding:0 30px;border-radius:999px;background:${AP.INK};color:${AP.BG};font-weight:800;letter-spacing:.04em;text-transform:uppercase;font-size:14px;cursor:pointer;border:none;transition:transform .18s,box-shadow .18s}
  .apx .btn:hover{transform:translateY(-2px)}
  .apx .btn.acc{background:${AP.GRAD};color:#fff;box-shadow:0 10px 30px rgba(163,102,255,.35)}
  .apx .btn.acc:disabled{opacity:.6;cursor:default;transform:none}
  .apx .btn.ghost{background:transparent;color:${AP.INK};border:1px solid ${AP.LINE}}
  .apx .btn.ghost:hover{border-color:${AP.ACC};color:#fff}
  .ap-tile{position:relative;display:block;overflow:hidden;background:${AP.S}}
  .ap-tile .ap-ph{transition:transform .6s cubic-bezier(.22,1,.36,1)}
  .ap-tile:hover .ap-ph{transform:scale(1.05)}
  .ap-tile .ap-alt{opacity:0;transition:opacity .55s ease}
  .ap-tile:hover .ap-alt{opacity:1}
  .ap-tile .ap-shop{position:absolute;left:0;right:0;bottom:0;padding:16px;opacity:0;transform:translateY(8px);transition:opacity .25s,transform .25s;background:linear-gradient(to top,rgba(4,4,8,.72),transparent)}
  .ap-tile:hover .ap-shop{opacity:1;transform:none}
  .sz{min-width:46px;height:44px;padding:0 10px;border:1px solid ${AP.LINE};background:transparent;color:${AP.INK};font-weight:700;font-size:13px;cursor:pointer;transition:border-color .15s,background .15s}
  .sz:hover{border-color:${AP.ACC}}
  .sz.on{background:${AP.INK};color:${AP.BG};border-color:${AP.INK}}
  @keyframes apIn{from{opacity:0}to{opacity:1}}
  @keyframes apSlide{from{transform:translateX(100%)}to{transform:translateX(0)}}
  @keyframes apPulse{0%,100%{opacity:.4}50%{opacity:.8}}
  .ap-skel{background:${AP.S2};animation:apPulse 1.4s ease-in-out infinite}
  .ap-rise{opacity:0;transform:translateY(20px);transition:opacity .7s ease,transform .7s cubic-bezier(.22,1,.36,1)}
  .ap-rise.in{opacity:1;transform:none}
  @media(prefers-reduced-motion:reduce){.ap-rise{opacity:1!important;transform:none!important;transition:none}.ap-skel{animation:none}}
  @media(max-width:820px){ .apx .apnav{display:none!important} .ap2{grid-template-columns:1fr!important} .apgrid{grid-template-columns:1fr 1fr!important} }
  /* ── admin dashboard ── */
  .adm-tabs{display:flex;gap:4px;border-bottom:1px solid ${AP.LINE};margin-bottom:28px;flex-wrap:wrap}
  .adm-tab{appearance:none;background:none;border:none;border-bottom:2px solid transparent;color:${AP.SUB};font-family:inherit;font-size:14px;font-weight:700;letter-spacing:.02em;padding:12px 16px;cursor:pointer;margin-bottom:-1px;transition:color .15s,border-color .15s}
  .adm-tab:hover{color:${AP.INK}}
  .adm-tab.on{color:${AP.INK};border-bottom-color:${AP.ACC}}
  .adm-card{background:${AP.S};border:1px solid ${AP.LINE};border-radius:14px;padding:20px 22px}
  .adm-stat{font-family:'Manrope',sans-serif;font-weight:800;letter-spacing:-.02em;font-size:30px;color:${AP.INK};line-height:1}
  .adm-lbl{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:${AP.SUB};font-weight:700;margin-top:8px}
  .adm-table{width:100%;border-collapse:collapse;font-size:14px}
  .adm-table th{text-align:left;font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:${AP.SUB};font-weight:700;padding:10px 12px;border-bottom:1px solid ${AP.LINE}}
  .adm-table td{padding:12px;border-bottom:1px solid ${AP.LINE};color:${AP.INK};vertical-align:middle}
  .adm-table tbody tr{cursor:pointer;transition:background .12s}
  .adm-table tbody tr:hover{background:${AP.S}}
  .adm-badge{display:inline-block;font-size:11px;font-weight:700;letter-spacing:.04em;padding:3px 9px;border-radius:999px;text-transform:uppercase}
  .adm-inp{width:100%;height:46px;background:${AP.S};border:1px solid ${AP.LINE};border-radius:10px;color:${AP.INK};font-family:inherit;font-size:15px;padding:0 14px;outline:none;transition:border-color .15s}
  .adm-inp:focus{border-color:${AP.ACC}}
  .adm-thumb{position:relative;width:84px;height:105px;border-radius:8px;overflow:hidden;border:1px solid ${AP.LINE};background:${AP.S2};flex:0 0 auto}
  .adm-thumb img{width:100%;height:100%;object-fit:cover;display:block}
  .adm-x{position:absolute;top:4px;right:4px;width:22px;height:22px;border-radius:6px;border:none;background:rgba(8,8,13,.78);color:#fff;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;line-height:1}
  .adm-main{position:absolute;bottom:4px;left:4px;right:4px;height:22px;border:none;border-radius:6px;background:rgba(8,8,13,.72);color:#fff;font-size:9px;letter-spacing:.06em;text-transform:uppercase;font-weight:700;cursor:pointer}
  .adm-main.is{background:${AP.GRAD}}
  .adm-drop{border:1.5px dashed ${AP.LINE};border-radius:10px;color:${AP.SUB};font-size:13px;font-weight:700;letter-spacing:.03em;display:flex;align-items:center;justify-content:center;width:84px;height:105px;cursor:pointer;text-align:center;transition:border-color .15s,color .15s}
  .adm-drop:hover{border-color:${AP.ACC};color:${AP.INK}}
`;

function BagDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, currency } = useCart();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!open) return null;
  const checkout = async () => {
    setBusy(true); setErr('');
    try {
      let tok = null; try { tok = localStorage.getItem('cs_token'); } catch {}
      const r = await fetch('/api/public/apparel/checkout', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', ...(tok ? { Authorization:`Bearer ${tok}` } : {}) },
        body: JSON.stringify({ items: items.map(i => ({ variant_id: Number(i.variant_id), quantity: i.qty })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.url) { window.location.href = d.url; return; }
      setErr(d.detail || 'Checkout is not available yet.'); setBusy(false);
    } catch { setErr('Checkout is not available yet.'); setBusy(false); }
  };
  return (
    <div style={{ position:'fixed',inset:0,zIndex:300,animation:'apIn .2s ease' }}>
      <div onClick={() => setOpen(false)} style={{ position:'absolute',inset:0,background:'rgba(2,2,5,.6)',backdropFilter:'blur(2px)' }} />
      <aside style={{ position:'absolute',top:0,right:0,bottom:0,width:'min(420px,92vw)',background:AP.BG,borderLeft:`1px solid ${AP.LINE}`,display:'flex',flexDirection:'column',animation:'apSlide .28s cubic-bezier(.22,1,.36,1)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'22px 24px',borderBottom:`1px solid ${AP.LINE}` }}>
          <span style={{ ...AP.DISP,fontSize:16,color:AP.INK,letterSpacing:'.04em' }}>Your bag</span>
          <button onClick={() => setOpen(false)} style={{ background:'none',border:'none',color:AP.SUB,fontSize:24,cursor:'pointer',lineHeight:1 }}>×</button>
        </div>
        <div style={{ flex:1,overflowY:'auto',padding:'8px 24px' }}>
          {items.length === 0 ? (
            <div style={{ textAlign:'center',padding:'60px 0',color:AP.SUB }}>
              <p style={{ ...AP.DISP,fontSize:15,color:AP.INK,marginBottom:8 }}>Your bag is empty</p>
              <p style={{ fontSize:13 }}>DROP 001 is waiting.</p>
            </div>
          ) : items.map(i => (
            <div key={i.key} style={{ display:'flex',gap:14,padding:'18px 0',borderBottom:`1px solid ${AP.LINE}` }}>
              <div style={{ width:56,height:70,borderRadius:6,background:AP.S2,border:`1px solid ${AP.LINE}`,flexShrink:0,overflow:'hidden' }}>
                {i.image && <img src={i.image} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />}
              </div>
              <div style={{ flex:1,minWidth:0 }}>
                <p style={{ ...AP.DISP,fontSize:13,color:AP.INK }}>{i.name}</p>
                <p style={{ fontSize:12,color:AP.SUB,margin:'3px 0 10px' }}>{i.size ? `Size ${i.size}` : ''}</p>
                <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                  <div style={{ display:'flex',alignItems:'center',border:`1px solid ${AP.LINE}`,borderRadius:6 }}>
                    <button onClick={() => setQty(i.key, i.qty - 1)} style={{ width:30,height:30,background:'none',border:'none',color:AP.INK,cursor:'pointer' }}>−</button>
                    <span style={{ minWidth:22,textAlign:'center',fontSize:13,fontWeight:700,color:AP.INK }}>{i.qty}</span>
                    <button onClick={() => setQty(i.key, i.qty + 1)} style={{ width:30,height:30,background:'none',border:'none',color:AP.INK,cursor:'pointer' }}>+</button>
                  </div>
                  <span style={{ marginLeft:'auto',fontSize:13,fontWeight:700,color:AP.INK }}>{money(i.price * i.qty, i.currency)}</span>
                </div>
                <button onClick={() => remove(i.key)} style={{ marginTop:8,background:'none',border:'none',color:AP.SUB,fontSize:11,letterSpacing:'.06em',textTransform:'uppercase',cursor:'pointer',padding:0 }}>Remove</button>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div style={{ padding:'20px 24px',borderTop:`1px solid ${AP.LINE}` }}>
            <div style={{ display:'flex',justifyContent:'space-between',marginBottom:16 }}>
              <span style={{ fontSize:13,letterSpacing:'.06em',textTransform:'uppercase',color:AP.SUB,fontWeight:600 }}>Subtotal</span>
              <span style={{ ...AP.DISP,fontSize:18,color:AP.INK }}>{money(subtotal, currency)}</span>
            </div>
            <button className="btn acc" style={{ width:'100%' }} onClick={checkout} disabled={busy}>{busy ? 'Redirecting…' : 'Checkout'}</button>
            {err && <p style={{ fontSize:12,color:AP.ACC2,textAlign:'center',marginTop:12,lineHeight:1.6 }}>{err}</p>}
            <p style={{ fontSize:11,color:AP.SUB,textAlign:'center',marginTop:10 }}>Secure payment · shipping calculated at checkout</p>
          </div>
        )}
      </aside>
    </div>
  );
}

export function ApparelShell({ children, active }) {
  const { count, setOpen } = useCart();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const els = document.querySelectorAll('.ap-rise');
    if (!('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('in')); return; }
    const io = new IntersectionObserver((es) => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { threshold:.1, rootMargin:'0px 0px -6% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  });
  return (
    <div className="apx" style={{ minHeight:'100vh',background:AP.BG,color:AP.INK,fontFamily:"'Manrope',sans-serif",overflowX:'hidden' }}>
      <style>{AP_CSS}</style>
      <header style={{ position:'sticky',top:0,zIndex:100,height:66,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 clamp(18px,5vw,48px)',borderBottom:`1px solid ${AP.LINE}`,background:'rgba(8,8,13,.82)',backdropFilter:'blur(16px)' }}>
        <div style={{ display:'flex',alignItems:'center',gap:30 }}>
          <Link to="/apparel" style={{ display:'inline-flex',alignItems:'center',gap:9 }}>
            <img src="/android-chrome-512x512.png" alt="" width={26} height={26} style={{ display:'block' }} />
            <span style={{ ...AP.DISP,fontSize:15,color:AP.INK,letterSpacing:'.02em' }}>CywerST <span style={{ color:AP.ACC }}>Apparel</span></span>
          </Link>
          <nav className="apnav" style={{ display:'flex',gap:26,alignItems:'center' }}>
            {AP_NAV.map(([k,l,to]) => <Link key={k} to={to} className={`nl${active===k?' on':''}`}>{l}</Link>)}
          </nav>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:12 }}>
          <Link to="/apparel/account" className="acct" title="My account" aria-label="My account">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="8" r="3.4"/><path d="M5 20c.5-4 3.5-6 7-6s6.5 2 7 6"/></svg>
          </Link>
          <button className="bag" onClick={() => setOpen(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 8h12l-1 12H7z"/><path d="M9 8a3 3 0 0 1 6 0"/></svg>
            Bag ({count})
          </button>
        </div>
      </header>
      {children}
      <BagDrawer />
      <footer style={{ borderTop:`1px solid ${AP.LINE}`,padding:'40px clamp(18px,5vw,48px)',marginTop:40 }}>
        <div style={{ maxWidth:1200,margin:'0 auto',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:16 }}>
          <span style={{ ...AP.DISP,fontSize:14,color:AP.INK }}>CywerST Apparel · DROP 001</span>
          <div style={{ display:'flex',gap:22 }}>
            <Link to="/produtos" className="nl">Software</Link>
            <Link to="/estudio" className="nl">Studio</Link>
            <Link to="/contacto" className="nl">Contact</Link>
          </div>
          <span style={{ fontSize:12,color:AP.SUB }}>© 2026 CywerStudio · Portugal</span>
        </div>
      </footer>
    </div>
  );
}

/* ── product tile ─────────────────────────────────────────────────── */
function Tile({ p }) {
  return (
    <Link to={`/apparel/${p.slug}`} className="ap-tile ap-rise" style={{ borderRadius:4 }}>
      <div style={{ position:'relative',aspectRatio:'4/5' }}>
        <div className="ap-ph" style={{ position:'absolute',inset:0 }}><Frame src={p.image} alt={p.name} /></div>
        {p.images && p.images[1] && <img className="ap-alt" src={p.images[1]} alt="" style={{ position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover' }} />}
        <div className="ap-shop"><span style={{ ...AP.DISP,fontSize:12,color:'#fff' }}>View →</span></div>
      </div>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'baseline',padding:'14px 2px 4px' }}>
        <span style={{ ...AP.DISP,fontSize:14,color:AP.INK,letterSpacing:'.02em' }}>{p.name}</span>
        <span style={{ fontSize:14,fontWeight:700,color:AP.SUB }}>{money(p.price, p.currency)}</span>
      </div>
    </Link>
  );
}

/* ══════════════════════ APPAREL HOME (DROP 001) ══════════════════ */
export function ApparelHome() {
  const { loading, configured, products } = useProducts();
  const { clear } = useCart();
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('checkout') === 'success') { setOk(true); clear(); window.history.replaceState({}, '', '/apparel'); }
  }, [clear]);

  return (
    <ApparelShell active="apparel">
      {ok && (
        <div style={{ background:AP.GRAD,color:'#fff',textAlign:'center',padding:'14px 20px',fontWeight:700,fontSize:14,letterSpacing:'.03em' }}>
          Order placed — thank you. We're sending it to production. A confirmation is on its way to your email.
        </div>
      )}

      {/* HERO — campaign */}
      <section style={{ position:'relative',minHeight:'clamp(560px,80vh,760px)',display:'flex',alignItems:'flex-end',overflow:'hidden',borderBottom:`1px solid ${AP.LINE}` }}>
        <div style={{ position:'absolute',inset:0,background:`radial-gradient(90% 70% at 78% 30%,rgba(163,102,255,.16),transparent 60%),linear-gradient(120deg,#0a0a12,#08080d)` }} />
        <div style={{ position:'absolute',right:'-4%',top:0,bottom:0,width:'52%',display:'flex',alignItems:'center',justifyContent:'center' }}>
          <Garment kind="hoodie" style={{ width:'78%',color:'rgba(244,243,247,.06)' }} />
        </div>
        <div style={{ position:'relative',zIndex:1,width:'100%',maxWidth:1200,margin:'0 auto',padding:'0 clamp(20px,5vw,48px) clamp(44px,7vw,80px)' }}>
          <div className="ap-rise" style={{ display:'inline-flex',alignItems:'center',gap:10,marginBottom:22 }}>
            <span style={{ fontSize:11,letterSpacing:'.3em',fontWeight:700,color:AP.ACC }}>CYWERST APPAREL</span>
            <span style={{ fontSize:11,letterSpacing:'.2em',fontWeight:700,color:AP.SUB,border:`1px solid ${AP.LINE}`,borderRadius:999,padding:'4px 10px' }}>DROP 001</span>
          </div>
          <h1 className="ap-rise" style={{ ...AP.DISP,fontSize:'clamp(48px,10vw,132px)',lineHeight:.92,color:AP.INK,margin:0 }}>
            Build /<br />Ship /<br /><span style={{ WebkitTextStroke:`1px ${AP.INK}`, color:'transparent' }}>Repeat.</span>
          </h1>
          <p className="ap-rise" style={{ fontSize:'clamp(15px,1.7vw,18px)',color:AP.SUB,fontWeight:500,maxWidth:420,marginTop:24,lineHeight:1.55 }}>
            Clothing for creators. Built by creators.
          </p>
          <div className="ap-rise" style={{ marginTop:30 }}><a href="#drop" className="btn acc">Shop Drop 001</a></div>
        </div>
      </section>

      {/* DROP 001 — product grid */}
      <section id="drop" style={{ maxWidth:1200,margin:'0 auto',padding:'clamp(48px,7vw,90px) clamp(20px,5vw,48px)' }}>
        <div className="ap-rise" style={{ marginBottom:34 }}>
          <p style={{ fontSize:12,letterSpacing:'.24em',fontWeight:700,color:AP.ACC,marginBottom:12 }}>DROP 001</p>
          <h2 style={{ ...AP.DISP,fontSize:'clamp(30px,5vw,56px)',lineHeight:1,color:AP.INK }}>Build / Ship / Repeat</h2>
        </div>

        {loading ? (
          <div className="apgrid" style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'clamp(10px,1.4vw,20px)' }}>
            {[0,1,2,3,4,5].map(i => <div key={i} className="ap-skel" style={{ aspectRatio:'4/5',borderRadius:4 }} />)}
          </div>
        ) : products.length > 0 ? (
          <div className="apgrid" style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'clamp(10px,1.4vw,20px)' }}>
            {products.map(p => <Tile key={p.slug} p={p} />)}
          </div>
        ) : (
          <div className="ap-rise" style={{ border:`1px dashed ${AP.LINE}`,borderRadius:10,padding:'60px 30px',textAlign:'center' }}>
            <p style={{ ...AP.DISP,fontSize:20,color:AP.INK,marginBottom:10 }}>Drop 001 · dropping soon</p>
            <p style={{ fontSize:15,color:AP.SUB,fontWeight:500,maxWidth:440,margin:'0 auto',lineHeight:1.6 }}>
              {configured ? 'The pieces are being prepared. Check back shortly.' : 'The store goes live the moment the drop is stocked. Come back soon.'}
            </p>
          </div>
        )}
      </section>

      {/* INDEPENDENT BY DESIGN */}
      <section style={{ borderTop:`1px solid ${AP.LINE}`,borderBottom:`1px solid ${AP.LINE}`,background:AP.S }}>
        <div className="ap2" style={{ maxWidth:1200,margin:'0 auto',display:'grid',gridTemplateColumns:'1.1fr .9fr',alignItems:'center',gap:'clamp(24px,4vw,60px)',padding:'clamp(48px,7vw,96px) clamp(20px,5vw,48px)' }}>
          <div className="ap-rise">
            <h2 style={{ ...AP.DISP,fontSize:'clamp(36px,6vw,72px)',lineHeight:.95,color:AP.INK }}>Independent<br />by design.</h2>
            <p style={{ fontSize:16,color:AP.SUB,fontWeight:500,lineHeight:1.7,maxWidth:440,marginTop:22 }}>CywerST was built independently. So was the clothing.</p>
            <p style={{ ...AP.DISP,fontSize:14,color:AP.ACC,letterSpacing:'.12em',marginTop:18 }}>Software. Games. Plugins. Apparel.</p>
          </div>
          <div className="ap-rise" style={{ position:'relative',aspectRatio:'4/5',borderRadius:6,overflow:'hidden',border:`1px solid ${AP.LINE}` }}>
            <Frame src={products.find(p => /hood/i.test(p.name))?.image || products[0]?.image} />
          </div>
        </div>
      </section>

      {/* BUILD / SHIP / REPEAT philosophy */}
      <section style={{ position:'relative',minHeight:'clamp(460px,64vh,640px)',display:'flex',alignItems:'center',overflow:'hidden' }}>
        <div style={{ position:'absolute',inset:0,background:`radial-gradient(80% 70% at 30% 40%,rgba(242,12,201,.10),transparent 60%),linear-gradient(120deg,#0a0a12,#08080d)` }} />
        <div style={{ position:'absolute',left:'-2%',top:0,bottom:0,width:'46%',display:'flex',alignItems:'center',justifyContent:'center' }}>
          <Garment kind="tee" style={{ width:'80%',color:'rgba(244,243,247,.05)' }} />
        </div>
        <div style={{ position:'relative',zIndex:1,maxWidth:1200,margin:'0 auto',width:'100%',padding:'clamp(48px,7vw,90px) clamp(20px,5vw,48px)',textAlign:'center' }}>
          <h2 className="ap-rise" style={{ ...AP.DISP,fontSize:'clamp(44px,9vw,120px)',lineHeight:.9,color:AP.INK }}>Build.<br />Ship.<br />Repeat.</h2>
          <p className="ap-rise" style={{ fontSize:12,letterSpacing:'.3em',fontWeight:700,color:AP.SUB,marginTop:20 }}>DROP 001 / CYWERST</p>
          <div className="ap-rise" style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:18,maxWidth:820,margin:'40px auto 0',textAlign:'left' }}>
            {[['Build','Turn ideas into something real.'],['Ship','Put your work into the world.'],['Repeat','Then do it again.']].map(([t,d]) => (
              <div key={t} style={{ border:`1px solid ${AP.LINE}`,borderRadius:8,padding:'22px 22px',background:'rgba(16,16,23,.5)' }}>
                <p style={{ ...AP.DISP,fontSize:18,color:AP.INK,marginBottom:8 }}>{t}</p>
                <p style={{ fontSize:14,color:AP.SUB,fontWeight:500,lineHeight:1.6 }}>{d}</p>
              </div>
            ))}
          </div>
          <div className="ap-rise" style={{ marginTop:36 }}><a href="#drop" className="btn ghost">Made for creators →</a></div>
        </div>
      </section>
    </ApparelShell>
  );
}

/* ══════════════════════ PRODUCT PAGE ═════════════════════════════ */
export function ApparelProduct() {
  const { slug } = useParams();
  const { loading, products } = useProducts();
  const { add } = useCart();
  const p = findBySlug(products, slug);
  const [variant, setVariant] = useState(null);
  const [warn, setWarn] = useState(false);
  const [mainImg, setMainImg] = useState(0);

  if (loading) {
    return <ApparelShell active="apparel"><div style={{ maxWidth:1200,margin:'0 auto',padding:'40px clamp(20px,5vw,48px)' }}><div className="ap-skel" style={{ aspectRatio:'2/1',borderRadius:8 }} /></div></ApparelShell>;
  }
  if (!p) {
    return <ApparelShell active="apparel"><div style={{ maxWidth:800,margin:'0 auto',padding:'120px 24px',textAlign:'center' }}>
      <p style={{ ...AP.DISP,fontSize:24,color:AP.INK,marginBottom:12 }}>Not in this drop</p>
      <Link to="/apparel" className="btn ghost">Back to Drop 001</Link>
    </div></ApparelShell>;
  }

  const sizes = p.variants || [];
  const gallery = (p.images && p.images.length ? p.images : [p.image]).filter(Boolean);
  const onAdd = () => {
    if (!variant) { setWarn(true); return; }
    add({ variant_id: variant.id, name: p.name, size: variant.size, color: variant.color, price: variant.price, currency: variant.currency || p.currency, image: variant.image || p.image });
  };

  return (
    <ApparelShell active="apparel">
      <div style={{ maxWidth:1200,margin:'0 auto',padding:'clamp(20px,3vw,32px) clamp(20px,5vw,48px) 0' }}>
        <Link to="/apparel" className="nl">← Drop 001</Link>
      </div>
      <section className="ap2" style={{ maxWidth:1200,margin:'0 auto',display:'grid',gridTemplateColumns:'1.15fr .85fr',gap:'clamp(24px,4vw,56px)',padding:'clamp(20px,3vw,32px) clamp(20px,5vw,48px) clamp(40px,6vw,64px)',alignItems:'start' }}>
        {/* gallery */}
        <div className="ap-rise">
          <div style={{ position:'relative',aspectRatio:'4/5',borderRadius:8,overflow:'hidden',border:`1px solid ${AP.LINE}` }}>
            <Frame src={gallery[mainImg]} alt={p.name} />
          </div>
          {gallery.length > 1 && (
            <div style={{ display:'flex',gap:10,marginTop:12,flexWrap:'wrap' }}>
              {gallery.map((ph, idx) => (
                <button key={idx} onClick={() => setMainImg(idx)} style={{ width:66,aspectRatio:'4/5',borderRadius:6,overflow:'hidden',border:`1px solid ${mainImg===idx?AP.ACC:AP.LINE}`,padding:0,cursor:'pointer',background:'none' }}>
                  <img src={ph} alt="" style={{ width:'100%',height:'100%',objectFit:'cover',display:'block' }} />
                </button>
              ))}
            </div>
          )}
        </div>
        {/* buy panel */}
        <div className="ap-rise" style={{ position:'sticky',top:90 }}>
          <p style={{ fontSize:11,letterSpacing:'.24em',fontWeight:700,color:AP.ACC,marginBottom:12 }}>DROP 001</p>
          <h1 style={{ ...AP.DISP,fontSize:'clamp(28px,3.6vw,42px)',lineHeight:1,color:AP.INK }}>{p.name}</h1>
          <p style={{ ...AP.DISP,fontSize:24,color:AP.INK,margin:'16px 0 26px',letterSpacing:0 }}>{money(variant ? variant.price : p.price, p.currency)}</p>

          {sizes.some(v => v.color) && (
            <>
              <p style={{ fontSize:12,letterSpacing:'.08em',textTransform:'uppercase',color:AP.SUB,fontWeight:700,marginBottom:10 }}>Color</p>
              <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:24 }}>
                <span style={{ fontSize:14,color:AP.INK,fontWeight:600 }}>{variant?.color || sizes[0].color}</span>
              </div>
            </>
          )}

          <p style={{ fontSize:12,letterSpacing:'.08em',textTransform:'uppercase',color:AP.SUB,fontWeight:700,marginBottom:10 }}>Size</p>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginBottom:8 }}>
            {sizes.map(v => <button key={v.id} className={`sz${variant?.id===v.id?' on':''}`} onClick={() => { setVariant(v); setWarn(false); if (v.image) { const gi = gallery.indexOf(v.image); if (gi >= 0) setMainImg(gi); } }}>{v.size || v.name}</button>)}
          </div>
          {warn && <p style={{ fontSize:12,color:AP.ACC2,marginBottom:10 }}>Pick a size first.</p>}

          <button className="btn acc" style={{ width:'100%',marginTop:16 }} onClick={onAdd}>Add to bag</button>
          <p style={{ fontSize:12,color:AP.SUB,marginTop:14,lineHeight:1.6 }}>Printed on demand · ships in 3–7 days. Secure Stripe checkout.</p>

          <div style={{ marginTop:34,borderTop:`1px solid ${AP.LINE}`,paddingTop:26 }}>
            <p style={{ ...AP.DISP,fontSize:15,color:AP.INK,marginBottom:16 }}>The details</p>
            <ul style={{ margin:0,paddingLeft:0,listStyle:'none',display:'flex',flexDirection:'column',gap:9 }}>
              {['Premium print-on-demand blank','CYWERST custom inside label','Fulfilled & shipped by Printful','Designed in Portugal'].map((d,i) => (
                <li key={i} style={{ display:'flex',gap:10,fontSize:14,color:AP.INK,fontWeight:500,lineHeight:1.4 }}>
                  <span style={{ color:AP.ACC,fontWeight:800 }}>—</span>{d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* more from the drop */}
      {products.length > 1 && (
        <section style={{ borderTop:`1px solid ${AP.LINE}`,maxWidth:1200,margin:'0 auto',padding:'clamp(40px,6vw,72px) clamp(20px,5vw,48px)' }}>
          <p className="ap-rise" style={{ ...AP.DISP,fontSize:'clamp(22px,3vw,32px)',color:AP.INK,marginBottom:24 }}>More from the drop</p>
          <div className="apgrid" style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'clamp(10px,1.4vw,20px)' }}>
            {products.filter(x => x.slug !== p.slug).slice(0,3).map(x => <Tile key={x.slug} p={x} />)}
          </div>
        </section>
      )}
    </ApparelShell>
  );
}

/* ══════════════════════ ADMIN DASHBOARD ═════════════════════════════
   /apparel/admin — gated by the shared CywerLog account (role owner /
   studio_admin). Orders (auto-sent to Printful on payment), revenue and
   a photo manager to override each product's gallery.
   ══════════════════════════════════════════════════════════════════ */
const _tok = () => { try { return localStorage.getItem('cs_token'); } catch { return null; } };
async function adminApi(path, { method = 'GET', body, form } = {}) {
  const headers = {};
  const t = _tok();
  if (t) headers.Authorization = `Bearer ${t}`;
  if (body && !form) headers['Content-Type'] = 'application/json';
  const res = await fetch(`/api${path}`, {
    method, headers,
    body: form ? form : (body ? JSON.stringify(body) : undefined),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ detail: 'Error' }));
    throw { status: res.status, detail: e.detail || 'Error' };
  }
  return res.json();
}
const centsMoney = (c, cur = 'eur') => money((Number(c) || 0) / 100, (cur || 'eur').toUpperCase());
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-IE', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch { return iso; }
};
const STATUS_STYLE = {
  submitted: { bg:'rgba(34,197,94,.14)', c:'#4ADE80' },
  pending:   { bg:'rgba(234,179,8,.14)', c:'#FACC15' },
  pf_error:  { bg:'rgba(242,12,201,.16)', c:'#F472D0' },
};
function Badge({ s }) {
  const st = STATUS_STYLE[s] || { bg:AP.S2, c:AP.SUB };
  return <span className="adm-badge" style={{ background:st.bg, color:st.c }}>{s || '—'}</span>;
}
function Loading() {
  return <div className="adm-card"><p style={{ color:AP.SUB, margin:0, fontSize:14 }}>Loading…</p></div>;
}

function AdminLogin() {
  const { login } = useAuth();
  const [f, setF] = useState({ email:'', password:'', code:'' });
  const [need2fa, setNeed2fa] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      await login(f.email.trim(), f.password, need2fa ? f.code.trim() : null);
    } catch (ex) {
      if (ex.detail === '2FA_REQUIRED') { setNeed2fa(true); setErr('Enter your 2FA code to continue.'); }
      else setErr(ex.detail || 'Login failed');
    } finally { setBusy(false); }
  };
  return (
    <ApparelShell active={null}>
      <section style={{ maxWidth:420, margin:'0 auto', padding:'clamp(48px,9vw,110px) 20px' }}>
        <h1 style={{ ...AP.DISP, fontSize:26, marginBottom:6 }}>Store admin</h1>
        <p style={{ color:AP.SUB, fontSize:14, marginBottom:26 }}>Sign in with your CywerStudio account.</p>
        <form onSubmit={submit} className="adm-card" style={{ display:'grid', gap:14 }}>
          <input className="adm-inp" type="email" placeholder="Email" value={f.email} onChange={e => setF(s => ({ ...s, email:e.target.value }))} required autoFocus />
          <input className="adm-inp" type="password" placeholder="Password" value={f.password} onChange={e => setF(s => ({ ...s, password:e.target.value }))} required />
          {need2fa && <input className="adm-inp" inputMode="numeric" placeholder="2FA code" value={f.code} onChange={e => setF(s => ({ ...s, code:e.target.value }))} autoFocus />}
          <button className="btn acc" style={{ width:'100%' }} disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
          {err && <p style={{ color: err.startsWith('Enter') ? AP.SUB : AP.ACC2, fontSize:13, textAlign:'center', margin:0 }}>{err}</p>}
        </form>
        <p style={{ marginTop:18, textAlign:'center' }}><Link to="/apparel" className="nl">← Back to store</Link></p>
      </section>
    </ApparelShell>
  );
}

function Overview({ stats }) {
  if (!stats) return <Loading />;
  const cards = [
    ['Revenue', centsMoney(stats.revenue_cents, stats.currency)],
    ['Orders', stats.orders],
    ['Items sold', stats.units],
  ];
  const byStatus = stats.by_status || {};
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:22 }}>
        {cards.map(([l, v]) => <div key={l} className="adm-card"><div className="adm-stat">{v}</div><div className="adm-lbl">{l}</div></div>)}
      </div>
      <div className="adm-card">
        <div className="adm-lbl" style={{ marginTop:0, marginBottom:14 }}>Orders by status</div>
        {Object.keys(byStatus).length === 0
          ? <p style={{ color:AP.SUB, fontSize:14, margin:0 }}>No orders yet.</p>
          : <div style={{ display:'flex', gap:18, flexWrap:'wrap' }}>{Object.entries(byStatus).map(([s, n]) => <span key={s} style={{ display:'inline-flex', alignItems:'center', gap:8 }}><Badge s={s} /><b style={{ color:AP.INK }}>{n}</b></span>)}</div>}
      </div>
    </div>
  );
}

function Section({ t, children }) {
  return <div style={{ borderTop:`1px solid ${AP.LINE}`, padding:'16px 0' }}><div className="adm-lbl" style={{ marginTop:0, marginBottom:10 }}>{t}</div>{children}</div>;
}
function OrderDetail({ o, onClose }) {
  const r = o.recipient || {};
  const addr = [r.address1, r.address2, [r.zip, r.city].filter(Boolean).join(' '), r.state_code, r.country_code].filter(Boolean);
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(4,4,8,.6)', backdropFilter:'blur(3px)', zIndex:200, display:'flex', justifyContent:'flex-end' }}>
      <aside onClick={e => e.stopPropagation()} style={{ width:'min(460px,94vw)', background:AP.BG, borderLeft:`1px solid ${AP.LINE}`, height:'100%', overflowY:'auto', animation:'apSlide .3s ease', padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
          <span style={{ ...AP.DISP, fontSize:16 }}>Order</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:AP.SUB, fontSize:20, cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}><Badge s={o.status} /><span style={{ fontSize:13, color:AP.SUB }}>{fmtDate(o.created)}</span></div>
        {o.status === 'pf_error' && o.error && <div className="adm-card" style={{ margin:'14px 0', borderColor:'rgba(242,12,201,.4)' }}><div className="adm-lbl" style={{ marginTop:0 }}>Printful error</div><p style={{ fontSize:12, color:AP.ACC2, margin:'8px 0 0', wordBreak:'break-word' }}>{o.error}</p></div>}
        <Section t="Items">{(o.items || []).map((it, i) => (
          <div key={i} style={{ display:'flex', gap:12, alignItems:'center', marginBottom:10 }}>
            <div className="adm-thumb" style={{ width:52, height:65 }}>{it.image ? <img src={it.image} alt="" /> : null}</div>
            <div style={{ flex:1 }}><div style={{ fontSize:14, color:AP.INK }}>{it.name}</div><div style={{ fontSize:12, color:AP.SUB }}>Qty {it.quantity}</div></div>
          </div>))}
        </Section>
        <Section t="Total"><div style={{ ...AP.DISP, fontSize:22 }}>{centsMoney(o.amount_total, o.currency)}</div></Section>
        <Section t="Ship to">
          <div style={{ fontSize:14, color:AP.INK, lineHeight:1.7 }}>{r.name}<br />{addr.map((l, i) => <span key={i}>{l}<br /></span>)}</div>
          {r.email && <div style={{ fontSize:13, color:AP.SUB, marginTop:6 }}>{r.email}</div>}
          {r.phone && <div style={{ fontSize:13, color:AP.SUB }}>{r.phone}</div>}
        </Section>
        <Section t="References">
          <div style={{ fontSize:12, color:AP.SUB, lineHeight:1.9, wordBreak:'break-all' }}>
            Printful order: {o.pf_order_id ? <a href="https://www.printful.com/dashboard/orders" target="_blank" rel="noreferrer" style={{ color:AP.ACC }}>#{o.pf_order_id}</a> : 'not created'}<br />
            Stripe session: {o.session_id}
          </div>
        </Section>
      </aside>
    </div>
  );
}
function Orders({ orders }) {
  const [sel, setSel] = useState(null);
  if (!orders || !orders.length) return <div className="adm-card"><p style={{ color:AP.SUB, margin:0, fontSize:14 }}>No orders yet. When a customer pays, the order shows up here and is sent to Printful automatically.</p></div>;
  return (
    <div>
      <div className="adm-card" style={{ padding:0, overflowX:'auto' }}>
        <table className="adm-table">
          <thead><tr><th>Date</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Printful</th></tr></thead>
          <tbody>{orders.map(o => (
            <tr key={o.session_id} onClick={() => setSel(o)}>
              <td style={{ whiteSpace:'nowrap', color:AP.SUB }}>{fmtDate(o.created)}</td>
              <td>{o.recipient?.name || '—'}<div style={{ fontSize:12, color:AP.SUB }}>{o.recipient?.email || ''}</div></td>
              <td>{(o.items || []).reduce((a, i) => a + (i.quantity || 0), 0)}</td>
              <td style={{ fontWeight:700 }}>{centsMoney(o.amount_total, o.currency)}</td>
              <td><Badge s={o.status} /></td>
              <td style={{ color:AP.SUB }}>{o.pf_order_id || '—'}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
      {sel && <OrderDetail o={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function ProductPhotos({ p }) {
  const [imgs, setImgs] = useState(p.images || []);
  const [custom, setCustom] = useState(p.custom);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  useEffect(() => { setImgs(p.images || []); setCustom(p.custom); }, [p]);
  const save = async (next) => {
    setBusy(true); setErr('');
    try { const r = await adminApi(`/apparel/admin/products/${p.id}/photos`, { method:'PUT', body:{ images:next } }); setImgs(r.images); setCustom(true); }
    catch (ex) { setErr(ex.detail || 'Failed'); } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ''; if (!file) return;
    setBusy(true); setErr('');
    try { const fd = new FormData(); fd.append('file', file); const r = await adminApi(`/apparel/admin/products/${p.id}/photos`, { method:'POST', form:fd }); setImgs(r.images); setCustom(true); }
    catch (ex) { setErr(ex.detail || 'Upload failed'); } finally { setBusy(false); }
  };
  const remove = (u) => save(imgs.filter(x => x !== u));
  const makeMain = (u) => save([u, ...imgs.filter(x => x !== u)]);
  const reset = async () => {
    if (!window.confirm('Reset this product to the Printful mockups? Your uploaded photos will be removed.')) return;
    setBusy(true); setErr('');
    try { const r = await adminApi(`/apparel/admin/products/${p.id}/photos`, { method:'DELETE' }); setImgs(r.images); setCustom(false); }
    catch (ex) { setErr(ex.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="adm-card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, gap:12, flexWrap:'wrap' }}>
        <div><span style={{ ...AP.DISP, fontSize:15 }}>{p.name}</span>{custom && <span className="adm-badge" style={{ marginLeft:10, background:'rgba(163,102,255,.14)', color:AP.ACC }}>custom</span>}</div>
        {custom && <button className="btn ghost" style={{ height:36, padding:'0 16px', fontSize:12 }} onClick={reset} disabled={busy}>Reset to Printful</button>}
      </div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', opacity:busy ? .5 : 1, pointerEvents:busy ? 'none' : 'auto' }}>
        {imgs.map((u, i) => (
          <div key={u} className="adm-thumb">
            <img src={u} alt="" />
            {imgs.length > 1 && <button className="adm-x" onClick={() => remove(u)} title="Remove">✕</button>}
            <button className={`adm-main${i === 0 ? ' is' : ''}`} onClick={() => i !== 0 && makeMain(u)}>{i === 0 ? 'Main' : 'Set main'}</button>
          </div>))}
        <div className="adm-drop" onClick={() => fileRef.current?.click()}>+ Add<br />photo</div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
      </div>
      {err && <p style={{ color:AP.ACC2, fontSize:12, marginTop:10 }}>{err}</p>}
    </div>
  );
}
function Photos({ products, configured }) {
  if (!configured) return <div className="adm-card"><p style={{ color:AP.SUB, margin:0, fontSize:14 }}>Printful isn't configured yet — no products to manage.</p></div>;
  if (!products || !products.length) return <div className="adm-card"><p style={{ color:AP.SUB, margin:0, fontSize:14 }}>No products in the Printful store yet. Publish products in Printful and they'll appear here.</p></div>;
  return (
    <div>
      <p style={{ color:AP.SUB, fontSize:13, marginTop:0, marginBottom:16, lineHeight:1.6 }}>Uploaded photos replace the store gallery for that product. The first photo is the main one shown on the grid.</p>
      <div style={{ display:'grid', gap:16 }}>{products.map(p => <ProductPhotos key={p.id} p={p} />)}</div>
    </div>
  );
}

function AdminDash() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState(null);
  const [prod, setProd] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setErr('');
    try {
      const [s, o, p] = await Promise.all([
        adminApi('/apparel/admin/stats'),
        adminApi('/apparel/admin/orders'),
        adminApi('/apparel/admin/products'),
      ]);
      setStats(s); setOrders(o.orders || []); setProd(p);
    } catch (ex) { setErr(ex.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <ApparelShell active={null}>
      <section style={{ maxWidth:1080, margin:'0 auto', padding:'clamp(28px,5vw,54px) clamp(18px,5vw,40px)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:26, gap:12, flexWrap:'wrap' }}>
          <div><h1 style={{ ...AP.DISP, fontSize:'clamp(24px,4vw,34px)', margin:0 }}>Store dashboard</h1><p style={{ color:AP.SUB, fontSize:13, margin:'6px 0 0' }}>Signed in as {user?.email}</p></div>
          <button className="btn ghost" style={{ height:38, padding:'0 18px', fontSize:12 }} onClick={logout}>Sign out</button>
        </div>
        <div className="adm-tabs">
          {[['overview','Overview'],['orders','Orders'],['photos','Photos']].map(([k, l]) => (
            <button key={k} className={`adm-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{l}{k === 'orders' && orders ? ` (${orders.length})` : ''}</button>))}
        </div>
        {err && <p style={{ color:AP.ACC2, fontSize:13, marginBottom:16 }}>{err} — <button onClick={load} style={{ background:'none', border:'none', color:AP.ACC, cursor:'pointer', textDecoration:'underline', font:'inherit' }}>retry</button></p>}
        {tab === 'overview' && <Overview stats={stats} />}
        {tab === 'orders' && (orders === null ? <Loading /> : <Orders orders={orders} />)}
        {tab === 'photos' && (prod === null ? <Loading /> : <Photos products={prod.products} configured={prod.configured} />)}
      </section>
    </ApparelShell>
  );
}

export function ApparelAdmin() {
  const { user, loading } = useAuth();
  if (loading) return <ApparelShell active={null}><section style={{ padding:'90px 20px', textAlign:'center', color:AP.SUB }}>Loading…</section></ApparelShell>;
  if (!user) return <AdminLogin />;
  if (!['owner', 'studio_admin'].includes(user.role)) return (
    <ApparelShell active={null}>
      <section style={{ maxWidth:440, margin:'0 auto', padding:'clamp(60px,10vw,120px) 20px', textAlign:'center' }}>
        <h1 style={{ ...AP.DISP, fontSize:24, marginBottom:10 }}>No access</h1>
        <p style={{ color:AP.SUB, fontSize:14, marginBottom:22 }}>This account isn't a store admin.</p>
        <Link to="/apparel" className="btn ghost" style={{ textDecoration:'none' }}>Back to store</Link>
      </section>
    </ApparelShell>
  );
  return <AdminDash />;
}

/* ══════════════════════ CUSTOMER ACCOUNT ═══════════════════════════
   /apparel/account — any signed-in shopper sees THEIR OWN orders and
   receipts. No admin data (no photos, no revenue, no other customers).
   Same CywerLog accounts; role is irrelevant here.
   ══════════════════════════════════════════════════════════════════ */
const CUST_PILL = {
  'In production': { bg:'rgba(34,197,94,.14)', c:'#4ADE80' },
  'Received':      { bg:'rgba(163,102,255,.14)', c:AP.ACC },
  'Processing':    { bg:'rgba(234,179,8,.14)', c:'#FACC15' },
};
function CustBadge({ s }) {
  const st = CUST_PILL[s] || { bg:AP.S2, c:AP.SUB };
  return <span className="adm-badge" style={{ background:st.bg, color:st.c }}>{s || '—'}</span>;
}

function AccountAuth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [f, setF] = useState({ name:'', email:'', password:'', code:'' });
  const [need2fa, setNeed2fa] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      if (mode === 'register') await register(f.name.trim(), f.email.trim(), f.password);
      else await login(f.email.trim(), f.password, need2fa ? f.code.trim() : null);
    } catch (ex) {
      if (ex.detail === '2FA_REQUIRED') { setNeed2fa(true); setErr('Enter your 2FA code to continue.'); }
      else setErr(ex.detail || (mode === 'register' ? 'Could not create account' : 'Login failed'));
    } finally { setBusy(false); }
  };
  return (
    <ApparelShell active={null}>
      <section style={{ maxWidth:420, margin:'0 auto', padding:'clamp(48px,9vw,100px) 20px' }}>
        <h1 style={{ ...AP.DISP, fontSize:26, marginBottom:6 }}>{mode === 'register' ? 'Create account' : 'Sign in'}</h1>
        <p style={{ color:AP.SUB, fontSize:14, marginBottom:22 }}>Track your orders and keep your receipts in one place.</p>
        <div className="adm-tabs" style={{ marginBottom:22 }}>
          <button className={`adm-tab${mode === 'login' ? ' on' : ''}`} onClick={() => { setMode('login'); setErr(''); }}>Sign in</button>
          <button className={`adm-tab${mode === 'register' ? ' on' : ''}`} onClick={() => { setMode('register'); setErr(''); setNeed2fa(false); }}>Create account</button>
        </div>
        <form onSubmit={submit} className="adm-card" style={{ display:'grid', gap:14 }}>
          {mode === 'register' && <input className="adm-inp" placeholder="Name" value={f.name} onChange={on('name')} required />}
          <input className="adm-inp" type="email" placeholder="Email" value={f.email} onChange={on('email')} required autoFocus={mode === 'login'} />
          <input className="adm-inp" type="password" placeholder={mode === 'register' ? 'Password (min. 8 characters)' : 'Password'} value={f.password} onChange={on('password')} required minLength={mode === 'register' ? 8 : undefined} />
          {need2fa && mode === 'login' && <input className="adm-inp" inputMode="numeric" placeholder="2FA code" value={f.code} onChange={on('code')} autoFocus />}
          <button className="btn acc" style={{ width:'100%' }} disabled={busy}>{busy ? 'Please wait…' : (mode === 'register' ? 'Create account' : 'Sign in')}</button>
          {err && <p style={{ color: err.startsWith('Enter') ? AP.SUB : AP.ACC2, fontSize:13, textAlign:'center', margin:0 }}>{err}</p>}
        </form>
        <p style={{ marginTop:18, textAlign:'center' }}><Link to="/apparel" className="nl">← Back to store</Link></p>
      </section>
    </ApparelShell>
  );
}

function CustomerOrder({ o }) {
  const s = o.ship_to || {};
  const addr = [s.address1, s.address2, [s.zip, s.city].filter(Boolean).join(' '), s.state_code, s.country_code].filter(Boolean);
  return (
    <div className="adm-card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}><CustBadge s={o.status} /><span style={{ fontSize:13, color:AP.SUB }}>{fmtDate(o.created)}</span></div>
        <span style={{ ...AP.DISP, fontSize:18 }}>{centsMoney(o.amount_total, o.currency)}</span>
      </div>
      <div style={{ display:'grid', gap:10, marginBottom:16 }}>
        {(o.items || []).map((it, i) => (
          <div key={i} style={{ display:'flex', gap:12, alignItems:'center' }}>
            <div className="adm-thumb" style={{ width:48, height:60 }}>{it.image ? <img src={it.image} alt="" /> : null}</div>
            <div style={{ flex:1 }}><div style={{ fontSize:14, color:AP.INK }}>{it.name}</div><div style={{ fontSize:12, color:AP.SUB }}>Qty {it.quantity}</div></div>
          </div>))}
      </div>
      {addr.length > 0 && (
        <div style={{ borderTop:`1px solid ${AP.LINE}`, paddingTop:12 }}>
          <div className="adm-lbl" style={{ marginTop:0, marginBottom:6 }}>Shipping to</div>
          <div style={{ fontSize:13, color:AP.SUB, lineHeight:1.6 }}>{s.name}{s.name ? ' · ' : ''}{addr.join(', ')}</div>
        </div>)}
    </div>
  );
}

function MyAccount() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setErr('');
    try { const d = await adminApi('/apparel/my/orders'); setOrders(d.orders || []); }
    catch (ex) { setErr(ex.detail || 'Could not load your orders'); setOrders([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <ApparelShell active={null}>
      <section style={{ maxWidth:720, margin:'0 auto', padding:'clamp(28px,5vw,54px) clamp(18px,5vw,40px)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:26, gap:12, flexWrap:'wrap' }}>
          <div><h1 style={{ ...AP.DISP, fontSize:'clamp(24px,4vw,32px)', margin:0 }}>My orders</h1><p style={{ color:AP.SUB, fontSize:13, margin:'6px 0 0' }}>{user?.name ? `${user.name} · ` : ''}{user?.email}</p></div>
          <button className="btn ghost" style={{ height:38, padding:'0 18px', fontSize:12 }} onClick={logout}>Sign out</button>
        </div>
        {err && <p style={{ color:AP.ACC2, fontSize:13, marginBottom:16 }}>{err} — <button onClick={load} style={{ background:'none', border:'none', color:AP.ACC, cursor:'pointer', textDecoration:'underline', font:'inherit' }}>retry</button></p>}
        {orders === null ? <Loading />
          : orders.length === 0
            ? <div className="adm-card" style={{ textAlign:'center', padding:'40px 24px' }}>
                <p style={{ color:AP.SUB, fontSize:14, margin:'0 0 18px', lineHeight:1.7 }}>No orders yet. When you buy something it shows up here — and a receipt is emailed to you.</p>
                <Link to="/apparel" className="btn acc" style={{ textDecoration:'none' }}>Shop the drop</Link>
              </div>
            : <div style={{ display:'grid', gap:16 }}>
                <p style={{ color:AP.SUB, fontSize:12, margin:0 }}>A receipt for each order was emailed to you at checkout.</p>
                {orders.map((o, i) => <CustomerOrder key={i} o={o} />)}
              </div>}
      </section>
    </ApparelShell>
  );
}

export function ApparelAccount() {
  const { user, loading } = useAuth();
  if (loading) return <ApparelShell active={null}><section style={{ padding:'90px 20px', textAlign:'center', color:AP.SUB }}>Loading…</section></ApparelShell>;
  if (!user) return <AccountAuth />;
  return <MyAccount />;
}
